import { useEffect, useState } from "react";
import NotFound from "@/pages/NotFound";
import { toast } from "sonner";
import { ImagePlus, Link2, Loader2, Send, Type, FlaskConical } from "lucide-react";
import { useAdmin } from "@/context/AdminContext";
import { api, errText } from "@/lib/api";
import { BlockEditor } from "@/components/newsletter/BlockEditor";
import { CampaignHistory } from "@/components/newsletter/CampaignHistory";

const DRAFT = "cm_email_draft";
const blank = (type) => ({ type, text: "", media_id: null, caption: "", label: "", url: "" });
const loadDraft = () => { try { return JSON.parse(localStorage.getItem(DRAFT)) || null; } catch (e) { return null; } };
const field = "w-full border border-black/20 bg-white px-4 py-3 text-base outline-none focus:border-[#0A0A0A]";

export default function SendEmailPage() {
  const { can, user } = useAdmin();
  const init = loadDraft();
  const [subject, setSubject] = useState(init?.subject || "");
  const [heading, setHeading] = useState(init?.heading || "");
  const [blocks, setBlocks] = useState(Array.isArray(init?.blocks) ? init.blocks : [blank("text")]);
  const [html, setHtml] = useState("");
  const [count, setCount] = useState(0);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState("");

  const loadHistory = () => api.get("/newsletter/campaigns").then((r) => setHistory(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  useEffect(() => {
    if (!can("send_email")) return undefined;
    loadHistory();
    api.get("/newsletter/audience-count").then((r) => setCount(r.data?.count || 0)).catch(() => {});
    return undefined;
  }, [can]);
  useEffect(() => {
    if (!history.some((c) => c.status === "sending")) return undefined;
    const t = setInterval(loadHistory, 3000);
    return () => clearInterval(t);
  }, [history]);
  useEffect(() => {
    localStorage.setItem(DRAFT, JSON.stringify({ subject, heading, blocks }));
    if (!can("send_email")) return undefined;
    const t = setTimeout(() => {
      api.post("/newsletter/preview", { subject: subject || "Preview", heading, blocks }).then((r) => setHtml(r.data?.html || "")).catch(() => {});
    }, 500);
    return () => clearTimeout(t);
  }, [subject, heading, blocks, can]);

  if (user === null) return <div className="min-h-[60vh]" />;
  if (!can("send_email")) return <NotFound />;

  const setAt = (i, patch) => setBlocks((bs) => bs.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const move = (i, d) => setBlocks((bs) => { const n = [...bs]; [n[i], n[i + d]] = [n[i + d], n[i]]; return n; });
  const payload = { subject: subject.trim(), heading, blocks };
  const ready = subject.trim() && (heading.trim() || blocks.length);

  const sendTest = async () => {
    setBusy("test");
    try { const r = await api.post("/newsletter/test", payload); toast.success(`Test email sent to ${r.data?.sent_to}`); } catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };
  const sendAll = async () => {
    if (!window.confirm(`Send "${subject.trim()}" to everyone on the mailing list (${count})?`)) return;
    setBusy("all");
    try {
      await api.post("/newsletter/send", payload);
      toast.success("Sending has started");
      setSubject(""); setHeading(""); setBlocks([blank("text")]); localStorage.removeItem(DRAFT);
      loadHistory();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };

  const addBtn = "flex items-center gap-2 border border-black/20 bg-white px-4 py-2.5 text-sm font-semibold transition-colors hover:bg-[#0A0A0A] hover:text-white";
  return (
    <div data-testid="send-email-page" className="mx-auto max-w-7xl px-5 pb-28 pt-10 sm:px-8 lg:px-12">
      <p className="font-mono-label text-xs uppercase tracking-[0.25em] text-[#57534E]">Private · only you can see this</p>
      <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">Send an email</h1>
      <p className="mt-3 text-sm text-[#57534E]" data-testid="send-email-recipient-count">Goes to everyone on your mailing list — {count} {count === 1 ? "person" : "people"}. Anyone who gave their name sees "Hi [their first name]," at the top.</p>

      <div className="mt-10 grid gap-10 lg:grid-cols-2">
        <div className="space-y-5">
          <label className="block"><span className="mb-1.5 block text-sm font-bold">Email subject (shown in the inbox)</span>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} placeholder="e.g. Big news from Cresway Media" className={field} data-testid="send-email-subject" /></label>
          <label className="block"><span className="mb-1.5 block text-sm font-bold">Heading (big title inside the email)</span>
            <input value={heading} onChange={(e) => setHeading(e.target.value)} maxLength={200} placeholder="e.g. Our new series drops Friday" className={field} data-testid="send-email-heading" /></label>
          <div className="space-y-3">
            {blocks.map((b, i) => (
              <BlockEditor key={i} b={b} i={i} count={blocks.length} set={(p) => setAt(i, p)} move={(d) => move(i, d)} remove={() => setBlocks((bs) => bs.filter((_, j) => j !== i))} />
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => setBlocks((bs) => [...bs, blank("text")])} className={addBtn} data-testid="send-email-add-text"><Type className="h-4 w-4" />Add text</button>
            <button type="button" onClick={() => setBlocks((bs) => [...bs, blank("image")])} className={addBtn} data-testid="send-email-add-image"><ImagePlus className="h-4 w-4" />Add picture</button>
            <button type="button" onClick={() => setBlocks((bs) => [...bs, blank("button")])} className={addBtn} data-testid="send-email-add-button"><Link2 className="h-4 w-4" />Add button link</button>
          </div>
          <div className="flex flex-wrap gap-3 border-t border-black/10 pt-6">
            <button type="button" onClick={sendTest} disabled={!ready || !!busy} data-testid="send-email-test"
              className="flex items-center gap-2 border border-[#0A0A0A] px-5 py-3 text-sm font-bold transition-colors hover:bg-[#0A0A0A] hover:text-white disabled:opacity-40">
              {busy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FlaskConical className="h-4 w-4" />}Send me a test
            </button>
            <button type="button" onClick={sendAll} disabled={!ready || !count || !!busy} data-testid="send-email-send-all"
              className="flex items-center gap-2 bg-[#E62314] px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-[#0A0A0A] disabled:opacity-40">
              {busy === "all" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}Send to everyone
            </button>
          </div>
        </div>
        <div className="lg:sticky lg:top-24 lg:self-start">
          <p className="mb-2 text-sm font-bold">Preview</p>
          <iframe title="Email preview" srcDoc={html} sandbox="" className="h-[640px] w-full border border-black/15 bg-[#F3F2EF]" data-testid="send-email-preview" />
        </div>
      </div>
      <CampaignHistory list={history} />
    </div>
  );
}
